/**
 * Promotion Auto-Assign Rules Service
 * CRUD for `promotion_state_rules` (the state -> offer source of truth that the
 * auto-assign engine reads) + the director mass-toggle RPC.
 *
 * Backed by migration 2026-06-25-002-promotion-auto-assign-engine.sql. The engine
 * ships DORMANT (sales_positions.auto_assign_promotions_enabled default false), so
 * nothing is auto-attached until a director is toggled on.
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

export type PromotionClassification = "insurance" | "non_insurance";

export interface PromotionStateRule {
  id: UUID;
  state_code: string;
  offer_id: UUID;
  is_active: boolean;
  applies_to_classification: PromotionClassification | null;
  created_at: string;
  updated_at: string;
  // Embedded offer (populated via the FK select)
  offer?: {
    id: UUID;
    name: string;
    offer_code: string;
    is_active: boolean;
  } | null;
}

export interface CreatePromotionStateRuleInput {
  state_code: string;
  offer_id: UUID;
  applies_to_classification?: PromotionClassification | null;
}

/**
 * List all promotion state rules with their embedded offer.
 */
export async function getPromotionStateRules(): Promise<PromotionStateRule[]> {
  const { data, error } = await supabase
    .from("promotion_state_rules")
    .select(
      "id, state_code, offer_id, is_active, applies_to_classification, created_at, updated_at, offer:offers(id, name, offer_code, is_active)",
    )
    .order("state_code", { ascending: true });

  if (error) {
    console.error("Error fetching promotion state rules:", error);
    throw error;
  }
  return (data || []) as unknown as PromotionStateRule[];
}

/**
 * Create a rule. (state_code, offer_id) is UNIQUE — a duplicate surfaces as 23505.
 */
export async function createPromotionStateRule(
  input: CreatePromotionStateRuleInput,
): Promise<PromotionStateRule> {
  const { data, error } = await supabase
    .from("promotion_state_rules")
    .insert({
      state_code: input.state_code,
      offer_id: input.offer_id,
      applies_to_classification: input.applies_to_classification ?? null,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating promotion state rule:", error);
    throw error;
  }
  return data as PromotionStateRule;
}

/**
 * Update a rule's active flag and/or classification keying.
 */
export async function updatePromotionStateRule(
  id: UUID,
  updates: {
    is_active?: boolean;
    applies_to_classification?: PromotionClassification | null;
  },
): Promise<PromotionStateRule> {
  const { data, error } = await supabase
    .from("promotion_state_rules")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating promotion state rule:", error);
    throw error;
  }
  return data as PromotionStateRule;
}

/**
 * Delete a rule. Existing auto-assigned event_offers are reconciled-out by the
 * engine on the next event change/toggle (and never affect already-issued vouchers).
 */
export async function deletePromotionStateRule(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("promotion_state_rules")
    .delete()
    .eq("id", id);

  if (error) {
    console.error("Error deleting promotion state rule:", error);
    throw error;
  }
}

/**
 * Flip a director's auto-assign engine on/off and re-run the engine over that
 * director's events. Returns the number of events processed. Authorization is
 * enforced server-side (admin/manager or the director themselves).
 */
export async function setDirectorAutoAssign(
  directorPositionId: UUID,
  enabled: boolean,
): Promise<number> {
  const { data, error } = await supabase.rpc("set_director_auto_assign", {
    p_director_position_id: directorPositionId,
    p_enabled: enabled,
  });

  if (error) {
    console.error("Error setting director auto-assign:", error);
    throw error;
  }
  return (data as number) ?? 0;
}
